"use strict";
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _a, _SmartContract_smartContractApi;
Object.defineProperty(exports, "__esModule", { value: true });
exports.SmartContract = void 0;
const viem_1 = require("viem");
const Error_1 = require("../Error");
const transactionStatus_1 = require("../types/api/transactionStatus");
const chain_1 = require("../utils/chain");
const error_1 = require("../utils/error");
const time_1 = require("../utils/time");
class SmartContract {
    constructor(id, title, abi, bin, chainId, deployment, createdAt, smartContractApi, rpcUrl, wallet) {
        this.id = id;
        this.title = title;
        this.abi = abi;
        this.bin = bin;
        this.chainId = chainId;
        this.deployment = deployment;
        this.createdAt = createdAt;
        this.smartContractApi = smartContractApi;
        this.rpcUrl = rpcUrl;
        this.wallet = wallet;
        _SmartContract_smartContractApi.set(this, void 0);
        this.write = async (functionName, props) => {
            if (!this.wallet) {
                throw new Error_1.KriptonioError({
                    message: 'please provide a wallet to write to the contract',
                });
            }
            if (!this.deployment) {
                throw new Error_1.KriptonioError({
                    message: 'please deploy the contract before writing to it',
                });
            }
            try {
                const data = (0, viem_1.encodeFunctionData)({
                    abi: this.abi,
                    functionName,
                    args: props?.params ?? [],
                });
                const tx = {
                    to: this.deployment.address,
                    data,
                    value: props?.value,
                    maxFeePerGas: props?.maxFeePerGas,
                    maxPriorityFeePerGas: props?.maxPriorityFeePerGas,
                };
                return await this.wallet.sendTransaction(tx, props?.options);
            }
            catch (e) {
                throw (0, error_1.parseError)(e);
            }
        };
        this.read = async (functionName, props) => {
            try {
                return (await this.readContract.read[functionName](props?.params ?? []));
            }
            catch (e) {
                throw (0, error_1.parseError)(e);
            }
        };
        this.estimate = async (functionName, props) => {
            try {
                if (!this.wallet) {
                    throw new Error_1.KriptonioError({
                        message: 'please attach a wallet to estimate the gas',
                    });
                }
                if (!this.deployment) {
                    throw new Error_1.KriptonioError({
                        message: 'please deploy the contract before estimating gas',
                    });
                }
                return await this.wallet.estimateGas({
                    to: this.deployment.address,
                    data: (0, viem_1.encodeFunctionData)({
                        abi: this.abi,
                        functionName,
                        args: props?.params ?? [],
                    }),
                });
            }
            catch (e) {
                throw (0, error_1.parseError)(e);
            }
        };
        this.estimateDeploy = async (props) => {
            if (!this.wallet) {
                throw new Error_1.KriptonioError({
                    message: 'please attach a wallet to estimate the deployment gas',
                });
            }
            try {
                return await this.wallet.estimateGas({
                    to: null,
                    data: (0, viem_1.encodeDeployData)({
                        abi: this.abi,
                        bytecode: (0, error_1.assertHex)(this.bin, 'bin'),
                        args: props?.params ?? [],
                    }),
                });
            }
            catch (e) {
                throw (0, error_1.parseError)(e);
            }
        };
        this.deploy = async (props) => {
            if (!this.wallet) {
                throw new Error_1.KriptonioError({
                    message: 'please provide a wallet to deploy the contract',
                });
            }
            try {
                const deploymentData = (0, viem_1.encodeDeployData)({
                    abi: this.abi,
                    bytecode: (0, error_1.assertHex)(this.bin, 'bin'),
                    args: props?.params ?? [],
                });
                const deployment = await this.wallet?.deployContract({
                    bytecode: deploymentData,
                }, props?.options);
                try {
                    this.deployment = _a.fromDeployment(await __classPrivateFieldGet(this, _SmartContract_smartContractApi, "f").createDeployment({
                        id: this.id,
                        data: {
                            address: deployment.address,
                            deployer: this.wallet.address,
                            transactionHash: deployment.hash,
                        },
                    }));
                }
                catch (e) {
                    console.error('error while creating deployment', e);
                }
                return {
                    hash: deployment.hash,
                    address: deployment.address,
                };
            }
            catch (e) {
                throw (0, error_1.parseError)(e);
            }
        };
        this.deployed = async (timeoutMs = 60000) => {
            if (!this.deployment?.transaction?.hash) {
                return false;
            }
            if (this.deployment.transaction.status === transactionStatus_1.TransactionStatus.Success) {
                return true;
            }
            try {
                let expiredTime = 0;
                while (expiredTime < timeoutMs) {
                    try {
                        const receipt = await this.publicClient.getTransactionReceipt({
                            hash: this.deployment.transaction.hash,
                        });
                        if (receipt.status === 'success') {
                            await this.smartContractApi.updateDeploymentTransaction(this.id, this.deployment.transaction.hash, {
                                success: true,
                            });
                            this.deployment.transaction.status = transactionStatus_1.TransactionStatus.Success;
                            return true;
                        }
                    }
                    catch (e) {
                        console.error('transient error while waiting for deployment transaction', e);
                    }
                    finally {
                        const sleepMs = 500;
                        await (0, time_1.sleep)(sleepMs);
                        expiredTime += sleepMs;
                    }
                }
                return false;
            }
            catch (e) {
                throw (0, error_1.parseError)(e);
            }
        };
        __classPrivateFieldSet(this, _SmartContract_smartContractApi, smartContractApi, "f");
    }
    get publicClient() {
        return (0, viem_1.createPublicClient)({
            transport: (0, viem_1.http)(this.rpcUrl, {
                batch: {
                    batchSize: 1000,
                    wait: 5,
                },
            }),
            chain: (0, chain_1.getChain)(this.chainId),
            batch: {
                multicall: true,
            },
        });
    }
    get readContract() {
        return (0, viem_1.getContract)({
            abi: this.abi,
            address: this.deployment?.address ?? '0x',
            client: this.publicClient,
        });
    }
}
exports.SmartContract = SmartContract;
_a = SmartContract, _SmartContract_smartContractApi = new WeakMap();
SmartContract.fromDto = (dto, smartContractApi, rpcUrl, wallet) => {
    return new _a(dto.id, dto.title, dto.abi, dto.bin, dto.chainId, dto.deployment ? _a.fromDeployment(dto.deployment) : undefined, new Date(dto.createdAt), smartContractApi, rpcUrl, wallet);
};
SmartContract.fromDeployment = (deployment) => {
    return {
        id: deployment.id,
        address: deployment.address,
        deployer: deployment.deployer,
        transaction: deployment.transaction,
    };
};
